import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SettingsCard from '../../organisms/SettingsCard';
import { FormInput, FormSelect } from '../../molecules/FormField';
import RoleMultiSelect from '../../molecules/RoleMultiSelect';
import Stepper from '../../organisms/Stepper';
import { SETTINGS_STEPS } from '../../../data/settingsSteps';
import {
  createComplaintType,
  fetchComplaintTypes,
  fetchComplaintTypeById,
  updateComplaintType,
} from '../../../redux/complaintType/actions';
import { fetchWorkflowSteps } from '../../../redux/Workflow/actions';
import { getFormFields } from '../../../redux/complaint/actions';
import { useSettingsWizard } from '../../../context/SettingsWizardContext';

import { setId } from '../../../redux/appDetails/reducer';
import {
  formatMinutesToDuration,
  isValidDurationInput,
  parseDurationToMinutes,
} from '../../../utils/duration';

const initialFormData = {
  categoryName: '',
  tatMinutes: '',
  template: '',
  categoryType: '',
  description: '',
  visibilityType: 'INTERNAL_LOGIN',
  urlLink: '',
  active: true,
  roles: [],
};

const COMPLAINT_TYPE_ACCESS_OPTIONS = [
  { value: 'INTERNAL_LOGIN', label: 'Internal Login' },
  { value: 'PRIVATE_LINK', label: 'Private Link' },
  { value: 'PUBLIC_LINK', label: 'Public Link' },
];

const UNSELECTED_TEMPLATE_OPTION = { value: '', label: 'Select Template' };

const getComplaintTypeValue = (complaintType) =>
  String(complaintType?.id ?? complaintType?.code ?? complaintType?.name ?? '');

const getComplaintTypeFormData = (complaintType) => ({
  categoryName: complaintType?.name ?? complaintType?.code ?? '',
  tatMinutes: formatMinutesToDuration(complaintType?.tatMinutes) || '',
  template: getComplaintTypeValue(complaintType),
  categoryType: complaintType?.categoryType ?? getComplaintTypeValue(complaintType),
  description: complaintType?.description ?? '',
  visibilityType: complaintType?.visibilityType ?? complaintType?.accessType ?? 'INTERNAL_LOGIN',
  urlLink: complaintType?.urlLink ?? '',
  active: complaintType?.isActive ?? complaintType?.active ?? true,
  roles: complaintType?.accesses?.map((access) => access?.role?.id) ?? complaintType?.roleIds ?? [],
});

function getTemplateSelectOptions(templateOptions) {
  return [UNSELECTED_TEMPLATE_OPTION, ...templateOptions];
}

function getCategoryTypeValidationMessage(validationErrors, formData) {
  const messages = [];
  if (validationErrors.categoryName) messages.push('Please enter a category name.');
  if (validationErrors.tatMinutes) {
    const tatValue = String(formData.tatMinutes ?? '').trim();
    messages.push(
      tatValue
        ? 'TAT must include a unit (m, h, or d). Example: 30m, 2h, 1d.'
        : 'Please enter TAT . Use 30m, 2h, or 1d.',
    );
  }
  if (validationErrors.visibilityType) messages.push('Please select a visibility type.');
  if (validationErrors.urlLink) messages.push('Please enter a URL link.');
  if (validationErrors.roles) messages.push('Please select roles for complaint filling.');
  return messages.join(' ');
}

export default function CategoryTypeStep({ stepMeta, stepperProps, roleList = [] }) {
  // Keep form values in this component so the form data stays on this page.
  // This is easier to understand than saving it in a separate shared context.
  const [formData, setFormData] = useState(initialFormData);
  const [validationErrors, setValidationErrors] = useState({});

  const dispatch = useDispatch();
  const { update } = useSettingsWizard();
  const { id } = useSelector((state) => state.appDetails || {});
  const {  isEdit } = useSelector((state) => state.appDetails || {});
  const complaintTypeState = useSelector((state) => state.complaintType || {});
  const { complaintList = [], saving, loading, error } = complaintTypeState;
  const hasRequestedTemplates = useRef(false);

  useEffect(() => {
    if (!complaintList.length && !loading && !hasRequestedTemplates.current) {
      hasRequestedTemplates.current = true;
      dispatch(fetchComplaintTypes());
    }
  }, [complaintList.length, dispatch, loading]);

useEffect(() => {
  // console.log(' useEffect:', {
  //   id,
  //   isEdit,
  // });

  if (!isEdit || id === null || id === undefined) {
    // console.log(' Not fetching');
    return;
  }

  // console.log(' Fetching complaint type:', id);

  dispatch(fetchComplaintTypeById(id))
    .unwrap()
    .then((response) => {
      // console.log(' API response for ID', id, ':', response);

      const data = response?.data ?? response;

      // console.log(' Data:', data);

      if (!data) return;

      const nextFormData = getComplaintTypeFormData(data);
      setFormData(nextFormData);
      update({ categoryRoleIds: nextFormData.roles });
    })
    .catch((error) => {
      console.log(' Fetch error:', error);
    });
   }, [dispatch, id, isEdit, update]);

  const RolesList = roleList.map((role) => ({
    value: role.id,
    label: role.name ?? role.label,
  }));

  const templateOptions = complaintList
    .map((complaintType) => ({
      value: getComplaintTypeValue(complaintType),
      label: complaintType?.name ?? complaintType?.code ?? '',
      complaintType,
    }))
    .filter((option) => option.value && option.label);



  const applyTemplateDetails = async (complaintType) => {
    const templateId = complaintType?.id ?? complaintType?._id ?? null;
    setFormData((prev) => ({
      ...prev,
      ...getComplaintTypeFormData(complaintType),
      categoryName: '',
      roles: [],
    }));
    update({
      templateId,
      tatMinutes: parseDurationToMinutes(complaintType?.tatMinutes),
    });
    if (templateId == null || templateId === '') {
      update({ prefillWorkflowSteps: [], prefillFormFields: [] });
      return;
    }
    try {
      const [workflowPayload, fieldsPayload] = await Promise.all([
        dispatch(fetchWorkflowSteps(templateId)).unwrap(),
        dispatch(getFormFields(templateId)).unwrap(),
      ]);
      const workflowSteps = (Array.isArray(workflowPayload?.data)
        ? workflowPayload.data
        : Array.isArray(workflowPayload)
          ? workflowPayload
          : []
      ).map((step) => ({
        ...step,
        roleIds: [],
        role: [],
        roleId: null,
      }));
      const formFields = Array.isArray(fieldsPayload?.data)
        ? fieldsPayload.data
        : Array.isArray(fieldsPayload)
          ? fieldsPayload
          : [];
      update({
        templateId,
        tatMinutes: parseDurationToMinutes(complaintType?.tatMinutes),
        prefillWorkflowSteps: workflowSteps,
        prefillFormFields: formFields,
      });
    } catch (error) {
      console.log('Failed to load template details:', error);
      update({
        templateId,
        prefillWorkflowSteps: [],
        prefillFormFields: [],
      });
    }
  };

  const clearSelectedTemplate = () => {
    setFormData((prev) => ({
      ...prev,
      template: '',
      categoryType: '',
      tatMinutes: '',
      description: '',
    }));
    update({
      templateId: null,
      prefillWorkflowSteps: [],
      prefillFormFields: [],
    });
  };

  const handleTemplateChange = (event) => {
    const value = event.target.value;
    if (!value) {
      clearSelectedTemplate();
      return;
    }

    const selectedTemplate = templateOptions.find((option) => option.value === value)?.complaintType;
    if (selectedTemplate) {
      applyTemplateDetails(selectedTemplate);
      return;
    }

    updateField('template', value);
    updateField('categoryType', value);
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setValidationErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const showUrlLink =
    formData.visibilityType === 'PRIVATE_LINK' ||
    formData.visibilityType === 'PUBLIC_LINK';

  const onSubmit = async () => {
    const nextValidationErrors = {};

    if (!formData.categoryName.trim()) nextValidationErrors.categoryName = true;
    if (!isValidDurationInput(formData.tatMinutes)) nextValidationErrors.tatMinutes = true;
    if (!formData.visibilityType) nextValidationErrors.visibilityType = true;
    if (!formData.roles.length) nextValidationErrors.roles = true;
    if (showUrlLink && !formData.urlLink.trim()) nextValidationErrors.urlLink = true;

    if (Object.keys(nextValidationErrors).length > 0) {
      setValidationErrors(nextValidationErrors);
      return;
    }

    setValidationErrors({});

    const tatMinutes = parseDurationToMinutes(formData.tatMinutes);
    update({ tatMinutes, categoryRoleIds: formData.roles });

    // This is the payload we want to send to the backend.
    // We build it from the local form state, not from a global store.
    const payload = {
      categoryName: formData.categoryName,
      categoryType: formData.categoryType || formData.template,
      tatMinutes,
      description: formData.description,
      visibilityType: formData.visibilityType,
      urlLink: showUrlLink ? formData.urlLink : '',
      active: formData.active,
      roles: formData.roles,
    };

    // console.log('CategoryTypeStep payload:', payload);
    // console.log("isedit ", isEdit, "id", id);

    const request = isEdit
      ? updateComplaintType({
          id,
          data: {
            code: payload.categoryName,
            name: payload.categoryName,
            description: payload.description,
            tatMinutes: payload.tatMinutes,
            accessType: payload.visibilityType,
            roleIds: payload.roles.map((role) => parseInt(role, 10)),
          },
          onSuccess: (data) => {
            console.log('updateComplaintType onSuccess:', data);
            stepperProps.onNext?.();
          },
          onFail: (error) => {
            console.log('updateComplaintType onFail:', error);
          },
        })
      : createComplaintType({ payload , onSuccess: (data) => {
        // console.log('createComplaintType onSuccess........................:', data);

        // setId(data?.data?.id ?? data?.id ?? null);
        // console.log("data response on creationg ", data);
        dispatch(setId(data?.data?.id ?? data?.id ?? null));
        // console.log(id, "system id set up successfully ..........", data?.data?.id ?? data?.id ?? null)
        stepperProps.onNext?.();
      }, onFail: (error) => {
        console.log('createComplaintType onFail:', error);
      }});

    const resultAction = dispatch(request);

    // Only move to next step if API call succeeded.
    if (
      (isEdit && updateComplaintType.fulfilled.match(resultAction)) ||
      (!isEdit && createComplaintType.fulfilled.match(resultAction))
    ) {
      if (!isEdit) {
        const createdId = resultAction.payload?.data?.id ?? resultAction.payload?.id;
        if (createdId !== null && createdId !== undefined) {
          // console.log("setting up  system id ..........", createdId)
          dispatch(setId(createdId));
      
          // console.log("system id set up successfully ..........", createdId)
        }
      }
      stepperProps.onNext?.();
    }
  };

  return (
    <>
      <Stepper
        {...stepperProps}
        steps={SETTINGS_STEPS}
        showNextButton={Boolean(stepperProps.showNextButton)}
        onNext={onSubmit}
      />
      {error || Object.keys(validationErrors).length > 0 ? (
        <div className="ca-validation-toast" role="alert">
          {error || getCategoryTypeValidationMessage(validationErrors, formData)}
        </div>
      ) : null}
      <SettingsCard
        className="ca-settings-card--category-type"
        title={stepMeta.title}
        description={stepMeta.description}
        showNext={false}
        headerExtra={(
          <div className="ca-category-type__header-tools">
            <div className="ca-category-type__template">
              <FormSelect
                label="Select Template (Optional)"
                value={formData.template || formData.categoryType}
                onChange={handleTemplateChange}
                options={getTemplateSelectOptions(templateOptions)}
                placeholder="Select Template"
                maxVisible={5}
                disabled={isEdit}
                className={isEdit ? 'ca-select--disabled' : ''}
              />
            </div>
          </div>
        )}
      >
        <div className="ca-step-section">
          <div className="ca-step-row ca-step-row--category-fields">
            <FormInput
              label={<>Category Name <span style={{ color: '#dc2626' }} aria-hidden="true">*</span></>}
              className="ca-step-row__grow"
              inputClassName={validationErrors.categoryName ? 'ca-control--invalid' : ''}
              value={formData.categoryName}
              onChange={(e) => updateField('categoryName', e.target.value)}
              placeholder="Enter category name"
            />

            <FormInput
              label={<>TAT <span style={{ color: '#dc2626' }} aria-hidden="true">*</span></>}
              type="text"
              className="ca-step-row__grow"
              inputClassName={validationErrors.tatMinutes ? 'ca-control--invalid' : ''}
              value={formData.tatMinutes}
              onChange={(e) => updateField('tatMinutes', e.target.value)}
              placeholder="Enter here"
              helperText="Eg: 30m, 2h, 1d, 3d7h16m"
            />
          </div>

          <div className="ca-step-row ca-step-row--category-fields">
            <FormInput
              label="Description"
              className="ca-step-row__grow"
              maxLength={500}
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Enter description"
            />
          </div>

          <div className="ca-step-row">
            <FormSelect
              label="Select Visibility Type"
              className={`ca-step-row__grow ${validationErrors.visibilityType ? 'ca-control--invalid' : ''}`}
              value={formData.visibilityType}
              onChange={(e) => updateField('visibilityType', e.target.value)}
              options={COMPLAINT_TYPE_ACCESS_OPTIONS}
              placeholder="Select visibility type"
            />

            {showUrlLink ? (
            <FormInput
              label={null}
              className="ca-step-row__grow ca-category-type__url-link"
              inputClassName={validationErrors.urlLink ? 'ca-control--invalid' : ''}
              value={formData.urlLink}
              onChange={(e) => updateField('urlLink', e.target.value)}
              placeholder="urllink"
            />
            ) : null}
          </div>
        </div>

        <div className="ca-step-section ca-step-section--last">
          <RoleMultiSelect
          label="Select Roles for Complaint Filling"
          required
          hasError={Boolean(validationErrors.roles)}
          options={RolesList}
          value={formData.roles}
          onChange={(roles) => updateField('roles', roles)}
          />
        </div>

        {loading ? <div className="ca-form-loading">Loading category...</div> : null}
        {saving ? <div className="ca-form-loading">Saving category...</div> : null}
      </SettingsCard>
    </>
  );
}
